import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    deletePlatformSupportSnapshot,
    PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT,
    type PlatformSupportSnapshotSlotState,
    uploadPlatformSupportSnapshot,
    validatePlatformSupportSnapshotPick,
} from "./platformSupportSnapshotUpload";

type UsePlatformSupportSnapshotsOptions = {
    invalidTypeMessage: string;
    tooLargeMessage: string;
    uploadFailedMessage: string;
};

export function usePlatformSupportSnapshots(options: UsePlatformSupportSnapshotsOptions) {
    const uploadSessionIdRef = useRef(crypto.randomUUID());
    const [slots, setSlots] = useState<PlatformSupportSnapshotSlotState[]>([]);
    const slotsRef = useRef(slots);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        slotsRef.current = slots;
    }, [slots]);

    const readySlots = useMemo(
        () => slots.filter((s) => s.status === "ready").map((s) => s.slot).sort((a, b) => a - b),
        [slots]
    );

    const resetSnapshots = useCallback(() => {
        setSlots((prev) => {
            prev.forEach((s) => URL.revokeObjectURL(s.previewUrl));
            return [];
        });
        uploadSessionIdRef.current = crypto.randomUUID();
    }, []);

    const abandonSnapshots = useCallback(async (): Promise<void> => {
        const sessionId = uploadSessionIdRef.current;
        const current = slotsRef.current;
        current.forEach((s) => URL.revokeObjectURL(s.previewUrl));
        setSlots([]);
        const slotsToDelete = current.filter((s) => s.status === "ready" || s.status === "uploading");
        await Promise.all(
            slotsToDelete.map((s) =>
                deletePlatformSupportSnapshot(sessionId, s.slot).catch(() => {
                    // Best-effort cleanup when leaving the form.
                })
            )
        );
        uploadSessionIdRef.current = crypto.randomUUID();
    }, []);

    const getUploadSessionId = useCallback(() => uploadSessionIdRef.current, []);

    const removeSnapshot = useCallback(
        async (slot: number): Promise<void> => {
            const entry = slots.find((s) => s.slot === slot);
            if (entry) {
                URL.revokeObjectURL(entry.previewUrl);
            }
            setSlots((prev) => prev.filter((s) => s.slot !== slot));
            try {
                await deletePlatformSupportSnapshot(uploadSessionIdRef.current, slot);
            } catch {
                // Best-effort; pending file may not exist if upload failed mid-way.
            }
        },
        [slots]
    );

    const getSnapshotForSlot = useCallback(
        (slot: number): PlatformSupportSnapshotSlotState | undefined => slots.find((s) => s.slot === slot),
        [slots]
    );

    const addSnapshotToSlot = useCallback(
        async (file: File, slot: number): Promise<void> => {
            if (slot < 0 || slot >= PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT) {
                return;
            }
            if (slots.some((s) => s.slot === slot)) {
                return;
            }

            const pickCode = validatePlatformSupportSnapshotPick(file);
            if (pickCode === "invalidType") {
                throw new Error(options.invalidTypeMessage);
            }
            if (pickCode === "tooLarge") {
                throw new Error(options.tooLargeMessage);
            }

            const previewUrl = URL.createObjectURL(file);
            setSlots((prev) => [...prev, { slot, previewUrl, status: "uploading" }]);
            setUploading(true);
            try {
                await uploadPlatformSupportSnapshot(uploadSessionIdRef.current, slot, file);
                setSlots((prev) =>
                    prev.map((s) => (s.slot === slot ? { ...s, status: "ready", errorMessage: undefined } : s))
                );
            } catch (error) {
                const message = error instanceof Error && error.message === "invalidType"
                    ? options.invalidTypeMessage
                    : error instanceof Error && error.message === "tooLarge"
                      ? options.tooLargeMessage
                      : options.uploadFailedMessage;
                setSlots((prev) =>
                    prev.map((s) => (s.slot === slot ? { ...s, status: "error", errorMessage: message } : s))
                );
                try {
                    await deletePlatformSupportSnapshot(uploadSessionIdRef.current, slot);
                } catch {
                    // ignore
                }
            } finally {
                setUploading(false);
            }
        },
        [options, slots]
    );

    return {
        getUploadSessionId,
        slots,
        readySlots,
        uploading,
        getSnapshotForSlot,
        addSnapshotToSlot,
        removeSnapshot,
        resetSnapshots,
        abandonSnapshots,
    };
}
